import { useMemo, useState } from "react";
import { Search, LogOut, Users, ChevronDown, Tag, Calendar, X } from "lucide-react";
import { thumbUrl } from "../lib/api";

const fallbackThumb =
  "https://images.unsplash.com/photo-1681311311317-a0561a8eef74?crop=entropy&cs=srgb&fm=jpg&w=600&q=70";

function Thumb({ rk, name }) {
  const [src, setSrc] = useState(thumbUrl(rk));
  return (
    <img
      src={src}
      alt={name}
      loading="lazy"
      onError={() => setSrc(fallbackThumb)}
      className="w-full aspect-video object-cover bg-[#1c1c1e]"
      data-testid="video-thumbnail"
    />
  );
}

// Subtle, refined accent dot per tag (Apple-style category colors).
const PALETTE = [
  "#0A84FF", "#30D158", "#FF9F0A", "#FF375F",
  "#BF5AF2", "#5E5CE6", "#64D2FF", "#FFD60A",
];
export function chipColor(tag) {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function TagPill({ label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[13px] font-medium text-white/90"
      data-testid="card-tag-chip"
    >
      <span className="h-2 w-2 rounded-full" style={{ background: chipColor(label) }} />
      {label}
    </span>
  );
}

const SORTS = {
  "name-asc": { label: "Name (A–Z)", fn: (a, b) => a.name.localeCompare(b.name) },
  "name-desc": { label: "Name (Z–A)", fn: (a, b) => b.name.localeCompare(a.name) },
  "year-desc": { label: "Year (newest)", fn: (a, b) => (b.year || 0) - (a.year || 0) || a.name.localeCompare(b.name) },
  "year-asc": { label: "Year (oldest)", fn: (a, b) => (a.year || 9999) - (b.year || 9999) || a.name.localeCompare(b.name) },
};

const decadeOf = (year) => Math.floor(year / 10) * 10;

export default function VideoList({ videos, onOpen, user, onSignOut, onManagePeople }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name-asc");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedDecades, setSelectedDecades] = useState([]);
  const [tagMatch, setTagMatch] = useState("any"); // any | all
  const [openPanel, setOpenPanel] = useState(null); // null | tags | years

  const allTags = useMemo(() => {
    const s = new Set();
    videos.forEach((v) => v.tags.forEach((t) => s.add(t)));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [videos]);

  const allDecades = useMemo(() => {
    const s = new Set();
    videos.forEach((v) => v.year && s.add(decadeOf(v.year)));
    return [...s].sort((a, b) => a - b);
  }, [videos]);

  const toggle = (setter) => (val) =>
    setter((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));
  const toggleTag = toggle(setSelectedTags);
  const toggleDecade = toggle(setSelectedDecades);

  const clearAll = () => {
    setSelectedTags([]);
    setSelectedDecades([]);
  };
  const activeFilters = selectedTags.length + selectedDecades.length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = videos.filter((v) => v.name.toLowerCase().includes(q));
    if (selectedTags.length > 0) {
      list = list.filter((v) =>
        tagMatch === "all"
          ? selectedTags.every((t) => v.tags.includes(t))
          : v.tags.some((t) => selectedTags.includes(t))
      );
    }
    if (selectedDecades.length > 0) {
      list = list.filter((v) => v.year && selectedDecades.includes(decadeOf(v.year)));
    }
    return [...list].sort(SORTS[sort].fn);
  }, [videos, query, selectedTags, selectedDecades, tagMatch, sort]);

  const FilterButton = ({ panel, icon: Icon, baseLabel, count }) => (
    <button
      onClick={() => setOpenPanel((p) => (p === panel ? null : panel))}
      className={`inline-flex h-12 items-center gap-2 rounded-xl px-4 text-[17px] font-medium transition-colors ${
        count > 0 ? "bg-[#0A84FF] text-white" : "bg-white/10 text-white hover:bg-white/15"
      }`}
      data-testid={`filter-${panel}-button`}
    >
      <Icon className="h-5 w-5" />
      {count > 0 ? `${count} ${baseLabel}${count > 1 ? "s" : ""}` : `Filter by ${baseLabel}`}
    </button>
  );

  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-30 border-b border-white/5">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-4 flex items-center gap-4">
          <h1
            className="text-2xl sm:text-[28px] font-bold tracking-tight text-white whitespace-nowrap"
            data-testid="app-title"
          >
            Kane Family Videos
          </h1>
          <div className="relative ml-auto w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              className="h-11 w-full rounded-xl bg-white/10 pl-11 pr-4 text-[17px] text-white placeholder-white/40 outline-none focus:bg-white/15 transition-colors"
              data-testid="search-input"
            />
          </div>
          {user?.role === "admin" && (
            <button
              onClick={onManagePeople}
              title="Manage people"
              className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
              data-testid="manage-people-button"
            >
              <Users className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={onSignOut}
            title={user?.email ? `Sign out (${user.email})` : "Sign out"}
            className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
            data-testid="sign-out-button"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-8 sm:py-10">
        {/* Controls */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-12 appearance-none rounded-xl bg-white/10 pl-4 pr-11 text-[17px] font-medium text-white outline-none hover:bg-white/15 focus:bg-white/15 transition-colors cursor-pointer"
              data-testid="sort-select"
            >
              {Object.entries(SORTS).map(([k, v]) => (
                <option key={k} value={k} className="bg-[#1c1c1e] text-white">
                  Sort: {v.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
          </div>

          {allTags.length > 0 && (
            <FilterButton panel="tags" icon={Tag} baseLabel="tag" count={selectedTags.length} />
          )}
          {allDecades.length > 0 && (
            <FilterButton panel="years" icon={Calendar} baseLabel="decade" count={selectedDecades.length} />
          )}
          {activeFilters > 0 && (
            <button
              onClick={clearAll}
              className="inline-flex h-12 items-center gap-1.5 rounded-xl bg-white/10 px-4 text-[17px] font-medium text-white hover:bg-white/15 transition-colors"
              data-testid="clear-filters-button"
            >
              <X className="h-5 w-5" /> Clear
            </button>
          )}
        </div>

        {/* Tag filter panel */}
        {openPanel === "tags" && allTags.length > 0 && (
          <div className="mb-7 animate-pop">
            <div className="mb-3 flex items-center gap-3">
              <span className="text-[15px] font-medium text-white/50">Show videos matching</span>
              <div className="inline-flex rounded-full bg-white/10 p-1" data-testid="tag-match-toggle">
                {["any", "all"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setTagMatch(m)}
                    className={`rounded-full px-4 py-1.5 text-[15px] font-semibold capitalize transition-colors ${
                      tagMatch === m ? "bg-[#0A84FF] text-white" : "text-white/70 hover:text-white"
                    }`}
                    data-testid={`tag-match-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <span className="text-[15px] text-white/40">of the selected tags</span>
            </div>
            <div className="flex flex-wrap gap-2.5" data-testid="filter-tag-list">
              {allTags.map((t) => {
                const active = selectedTags.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleTag(t)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-lg font-medium transition-colors ${
                      active ? "bg-[#0A84FF] text-white" : "bg-white/[0.08] text-white ring-1 ring-white/10 hover:bg-white/[0.16]"
                    }`}
                    data-testid="filter-tag-chip"
                  >
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: chipColor(t) }} />
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Decade filter panel */}
        {openPanel === "years" && allDecades.length > 0 && (
          <div className="mb-7 animate-pop" data-testid="filter-decade-list">
            <p className="mb-3 text-[15px] font-medium text-white/50">Pick one or more decades</p>
            <div className="flex flex-wrap gap-2.5">
              {allDecades.map((d) => {
                const active = selectedDecades.includes(d);
                return (
                  <button
                    key={d}
                    onClick={() => toggleDecade(d)}
                    className={`rounded-full px-5 py-2.5 text-lg font-medium transition-colors ${
                      active ? "bg-[#0A84FF] text-white" : "bg-white/[0.08] text-white ring-1 ring-white/10 hover:bg-white/[0.16]"
                    }`}
                    data-testid="filter-decade-chip"
                  >
                    {d}s
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-[15px] font-medium uppercase tracking-[0.12em] text-white/40 mb-5">
          {visible.length} {visible.length === 1 ? "Video" : "Videos"}
        </p>

        {visible.length === 0 ? (
          <p className="py-24 text-center text-xl text-white/50" data-testid="no-results">
            No videos match your search or filters.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-9">
            {visible.map((v, i) => (
              <button
                key={v.ratingKey}
                onClick={() => onOpen(v)}
                className="group text-left animate-fade-up"
                style={{ animationDelay: `${Math.min(i * 30, 350)}ms` }}
                data-testid="video-list-card"
              >
                <div className="tv-card overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-xl shadow-black/40">
                  <Thumb rk={v.ratingKey} name={v.name} />
                </div>
                <div className="mt-3 flex items-baseline gap-2 px-0.5">
                  <h2 className="text-[19px] font-semibold leading-tight text-white truncate">
                    {v.name}
                  </h2>
                  {v.year ? (
                    <span className="flex-shrink-0 text-[15px] font-medium text-white/40" data-testid="card-year">
                      {v.year}
                    </span>
                  ) : null}
                </div>
                {v.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5 px-0.5">
                    {v.tags.map((t) => (
                      <TagPill key={t} label={t} />
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
